import React, { useState, useEffect, useRef } from 'react';
import { 
    Sun, Cloud, CloudRain, CloudLightning, CloudSnow, Wind,
    Thermometer, Droplets, AlertTriangle, Bell, BellOff,
    ChevronRight, MapPin, RefreshCw, Loader2, Umbrella,
    ThermometerSun, CloudSun, Moon, Search, Navigation, Check,
    Waves, Activity, Gauge
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';

// Weather code to icon mapping
const getWeatherIcon = (code, size = "h-8 w-8") => {
    if (code === 0) return <Sun className={`${size} text-amber-500`} />;
    if (code >= 1 && code <= 3) return <CloudSun className={`${size} text-blue-400`} />;
    if (code >= 45 && code <= 48) return <Cloud className={`${size} text-gray-400`} />;
    if (code >= 51 && code <= 67) return <CloudRain className={`${size} text-blue-500`} />;
    if (code >= 71 && code <= 77) return <CloudSnow className={`${size} text-blue-200`} />;
    if (code >= 80 && code <= 82) return <CloudRain className={`${size} text-blue-600`} />;
    if (code >= 95) return <CloudLightning className={`${size} text-purple-500`} />;
    return <Cloud className={`${size} text-gray-400`} />;
};

const getWeatherDescription = (code) => {
    if (code === 0) return "Ensoleillé";
    if (code >= 1 && code <= 3) return "Partiellement nuageux";
    if (code >= 45 && code <= 48) return "Brouillard";
    if (code >= 51 && code <= 55) return "Bruine";
    if (code >= 56 && code <= 57) return "Bruine verglaçante";
    if (code >= 61 && code <= 65) return "Pluie";
    if (code >= 66 && code <= 67) return "Pluie verglaçante";
    if (code >= 71 && code <= 77) return "Neige";
    if (code >= 80 && code <= 82) return "Averses";
    if (code >= 85 && code <= 86) return "Averses de neige";
    if (code >= 95) return "Orage";
    return "Variable";
};

const getUVLevel = (uv) => {
    if (uv < 3) return { level: "Faible", color: "bg-green-500" };
    if (uv < 6) return { level: "Modéré", color: "bg-yellow-500" };
    if (uv < 8) return { level: "Élevé", color: "bg-orange-500" };
    if (uv < 11) return { level: "Très élevé", color: "bg-red-500" };
    return { level: "Extrême", color: "bg-purple-500" };
};

const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (date.toDateString() === tomorrow.toDateString()) return "Demain";
    
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
};

export default function WeatherPage() {
    const { user } = useAuth();
    const [weather, setWeather] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
    const [location, setLocation] = useState({ latitude: 43.5667, longitude: 6.4167, city_name: "Flayosc, Var" });
    
    // City search state
    const [showLocationPopover, setShowLocationPopover] = useState(false);
    const [citySearch, setCitySearch] = useState('');
    const [cityResults, setCityResults] = useState([]);
    const [searchingCity, setSearchingCity] = useState(false);
    const [detectingLocation, setDetectingLocation] = useState(false);
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        loadUserLocation();
        checkWelcomeDialog();
    }, [user]);

    const loadUserLocation = async () => {
        try {
            // Try to get user's saved location
            const savedLocation = await apiRequest('/user/weather-location');
            if (savedLocation && savedLocation.latitude) {
                setLocation(savedLocation);
                await fetchWeather(savedLocation);
            } else {
                await fetchWeather(location);
            }
        } catch (error) {
            // Default to Flayosc
            await fetchWeather(location);
        }
    };

    const checkWelcomeDialog = async () => {
        if (user) {
            try {
                const settings = await apiRequest('/user/settings/notifications');
                setNotificationsEnabled(settings.weather_alerts !== false);
                if (!settings.welcome_shown) {
                    setShowWelcomeDialog(true);
                }
            } catch (e) {
                // Not logged in or error, show welcome anyway for first time
                const shown = localStorage.getItem('weather_welcome_shown');
                if (!shown) {
                    setShowWelcomeDialog(true);
                }
            }
        } else {
            const shown = localStorage.getItem('weather_welcome_shown');
            if (!shown) {
                setShowWelcomeDialog(true);
            }
        }
    };

    const handleWelcomeAccept = async () => {
        // Request notification permission
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                toast.success('Notifications activées !');
            }
        }
        
        // Save preference
        if (user) {
            try {
                await apiRequest('/user/settings/notifications', {
                    method: 'POST',
                    body: JSON.stringify({ weather_alerts: true, welcome_shown: true })
                });
            } catch (e) {}
        }
        localStorage.setItem('weather_welcome_shown', 'true');
        setShowWelcomeDialog(false);
    };

    const handleWelcomeDecline = async () => {
        if (user) {
            try {
                await apiRequest('/user/settings/notifications', {
                    method: 'POST',
                    body: JSON.stringify({ weather_alerts: false, welcome_shown: true })
                });
            } catch (e) {}
        }
        localStorage.setItem('weather_welcome_shown', 'true');
        setNotificationsEnabled(false);
        setShowWelcomeDialog(false);
    };

    // City search functions
    const handleCitySearch = async (query) => {
        setCitySearch(query);
        
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        if (query.length < 2) {
            setCityResults([]);
            return;
        }
        
        // Debounce the search
        searchTimeoutRef.current = setTimeout(async () => {
            setSearchingCity(true);
            try {
                const data = await apiRequest(`/weather/search-city?query=${encodeURIComponent(query)}`);
                setCityResults(data.results || []);
            } catch (error) {
                console.error('City search error:', error);
                setCityResults([]);
            } finally {
                setSearchingCity(false);
            }
        }, 300);
    };

    const selectCity = async (city) => {
        const newLocation = {
            latitude: city.latitude,
            longitude: city.longitude,
            city_name: city.display_name
        };
        
        setLocation(newLocation);
        setShowLocationPopover(false);
        setCitySearch('');
        setCityResults([]);
        
        // Save to user profile if logged in
        if (user) {
            try {
                await apiRequest('/user/weather-location', {
                    method: 'POST',
                    body: JSON.stringify(newLocation)
                });
                toast.success('Localisation enregistrée');
            } catch (error) {
                console.error('Save location error:', error);
            }
        }
        
        // Fetch weather for new location
        await fetchWeather(newLocation);
    };

    const detectCurrentLocation = async () => {
        if (!navigator.geolocation) {
            toast.error('La géolocalisation n\'est pas supportée par votre navigateur');
            return;
        }
        
        setDetectingLocation(true);
        
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                
                // Reverse geocode to get city name
                try {
                    const response = await fetch(
                        `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${latitude}&longitude=${longitude}&language=fr`
                    );
                    const data = await response.json();
                    
                    let cityName = "Ma position";
                    if (data.results && data.results.length > 0) {
                        const result = data.results[0];
                        cityName = `${result.name}, ${result.admin1 || result.country}`;
                    }
                    
                    const newLocation = {
                        latitude,
                        longitude,
                        city_name: cityName
                    };
                    
                    setLocation(newLocation);
                    
                    // Save to user profile if logged in
                    if (user) {
                        try {
                            await apiRequest('/user/weather-location', {
                                method: 'POST',
                                body: JSON.stringify(newLocation)
                            });
                        } catch (e) {}
                    }
                    
                    await fetchWeather(newLocation);
                    setShowLocationPopover(false);
                    toast.success('Position détectée !');
                } catch (error) {
                    // Use coordinates even if reverse geocoding fails
                    const newLocation = {
                        latitude,
                        longitude,
                        city_name: "Ma position"
                    };
                    setLocation(newLocation);
                    await fetchWeather(newLocation);
                    setShowLocationPopover(false);
                }
                
                setDetectingLocation(false);
            },
            (error) => {
                setDetectingLocation(false);
                toast.error('Impossible de détecter votre position');
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const loadWeather = async () => {
        setLoading(true);
        await fetchWeather(location);
    };

    const fetchWeather = async (loc) => {
        try {
            const data = await apiRequest('/weather', {
                method: 'POST',
                body: JSON.stringify(loc)
            });
            setWeather(data);
            
            // Show notifications for alerts if enabled
            if (notificationsEnabled && data.alerts?.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
                data.alerts.forEach(alert => {
                    new Notification(`🏊 ${alert.title}`, {
                        body: alert.message,
                        icon: '/favicon.ico'
                    });
                });
            }
        } catch (error) {
            toast.error('Erreur météo');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchWeather(location);
    };

    const toggleNotifications = async () => {
        const newValue = !notificationsEnabled;
        setNotificationsEnabled(newValue);
        
        if (newValue && 'Notification' in window) {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                toast.error('Les notifications sont bloquées par votre navigateur');
                setNotificationsEnabled(false);
                return;
            }
        }
        
        if (user) {
            try {
                await apiRequest('/user/settings/notifications', {
                    method: 'POST',
                    body: JSON.stringify({ weather_alerts: newValue, welcome_shown: true })
                });
                toast.success(newValue ? 'Notifications activées' : 'Notifications désactivées');
            } catch (e) {
                toast.error('Erreur lors de la mise à jour');
            }
        } else {
            toast.success(newValue ? 'Notifications activées' : 'Notifications désactivées');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="weather-page">
            {/* Welcome Dialog */}
            <Dialog open={showWelcomeDialog} onOpenChange={setShowWelcomeDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <div className="flex justify-center mb-4">
                            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                                <Bell className="h-8 w-8 text-primary" />
                            </div>
                        </div>
                        <DialogTitle className="text-center text-xl">
                            Alertes Météo Piscine
                        </DialogTitle>
                        <DialogDescription className="text-center pt-2">
                            Water Analyzer souhaite vous aider à anticiper les variations de votre eau. 
                            Autorisez les notifications pour recevoir nos alertes météo et conseils de dosage personnalisés.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-3 mt-4">
                        <Button onClick={handleWelcomeAccept} className="rounded-full">
                            <Bell className="h-4 w-4 mr-2" />
                            Autoriser les notifications
                        </Button>
                        <Button variant="ghost" onClick={handleWelcomeDecline} className="rounded-full">
                            Plus tard
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold flex items-center gap-3">
                            <Sun className="h-10 w-10 text-amber-500" />
                            Météo Piscine
                        </h1>
                        <Popover open={showLocationPopover} onOpenChange={setShowLocationPopover}>
                            <PopoverTrigger asChild>
                                <button 
                                    className="text-muted-foreground mt-1 flex items-center gap-2 hover:text-foreground transition-colors cursor-pointer"
                                    data-testid="change-location-btn"
                                >
                                    <MapPin className="h-4 w-4" />
                                    {location.city_name || "Flayosc, Var"}
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80" align="start">
                                <div className="space-y-4">
                                    <h4 className="font-medium">Changer de localisation</h4>
                                    
                                    {/* Detect location button */}
                                    <Button 
                                        variant="outline" 
                                        className="w-full justify-start"
                                        onClick={detectCurrentLocation}
                                        disabled={detectingLocation}
                                        data-testid="detect-location-btn"
                                    >
                                        {detectingLocation ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Navigation className="h-4 w-4 mr-2" />
                                        )}
                                        Utiliser ma position
                                    </Button>
                                    
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Rechercher une ville..."
                                            value={citySearch}
                                            onChange={(e) => handleCitySearch(e.target.value)}
                                            className="pl-10"
                                            data-testid="city-search-input"
                                        />
                                    </div>
                                    
                                    {/* Search results */}
                                    {searchingCity && (
                                        <div className="flex items-center justify-center py-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        </div>
                                    )}
                                    
                                    {cityResults.length > 0 && (
                                        <div className="max-h-48 overflow-y-auto space-y-1">
                                            {cityResults.map((city, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => selectCity(city)}
                                                    className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors text-sm flex items-center gap-2"
                                                    data-testid={`city-result-${index}`}
                                                >
                                                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                                    <span className="truncate">{city.display_name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {citySearch.length >= 2 && !searchingCity && cityResults.length === 0 && (
                                        <p className="text-sm text-muted-foreground text-center py-2">
                                            Aucune ville trouvée
                                        </p>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="rounded-full"
                    >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>

                {/* Alerts */}
                {weather?.alerts?.length > 0 && (
                    <div className="space-y-3 mb-8">
                        {weather.alerts.map((alert, index) => {
                            const styles = {
                                storm: { border: 'border-purple-500/50', bg: 'bg-purple-500/10', ring: 'bg-purple-500/20', icon: <CloudLightning className="h-6 w-6 text-purple-500" /> },
                                heatwave: { border: 'border-red-500/50', bg: 'bg-red-500/10', ring: 'bg-red-500/20', icon: <ThermometerSun className="h-6 w-6 text-red-500" /> },
                                uv: { border: 'border-amber-500/50', bg: 'bg-amber-500/10', ring: 'bg-amber-500/20', icon: <Sun className="h-6 w-6 text-amber-500" /> },
                                rain_soon: { border: 'border-sky-500/50', bg: 'bg-sky-500/10', ring: 'bg-sky-500/20', icon: <CloudRain className="h-6 w-6 text-sky-500" /> },
                                evaporation: { border: 'border-teal-500/50', bg: 'bg-teal-500/10', ring: 'bg-teal-500/20', icon: <Droplets className="h-6 w-6 text-teal-500" /> },
                                cold_water: { border: 'border-blue-500/50', bg: 'bg-blue-500/10', ring: 'bg-blue-500/20', icon: <CloudSnow className="h-6 w-6 text-blue-500" /> },
                            };
                            const s = styles[alert.type] || styles.uv;
                            return (
                                <Card
                                    key={index}
                                    className={`border-2 ${s.border} ${s.bg} rounded-2xl`}
                                    data-testid={`alert-${alert.type}`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-4">
                                            <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${s.ring}`}>
                                                {s.icon}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-semibold">{alert.title}</h3>
                                                    <Badge variant="outline" className="text-xs">
                                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                                        Alerte
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    {alert.message}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Current Weather */}
                <Card className="glass rounded-3xl mb-6 overflow-hidden" data-testid="current-weather">
                    <CardContent className="p-6 sm:p-8">
                        <div className="grid md:grid-cols-2 gap-6 items-center">
                            <div className="flex items-center gap-6">
                                {getWeatherIcon(weather?.current?.weather_code, "h-24 w-24")}
                                <div>
                                    <p className="text-6xl font-bold leading-none">
                                        {Math.round(weather?.current?.temperature || 0)}°
                                    </p>
                                    <p className="text-muted-foreground text-lg mt-1">
                                        {getWeatherDescription(weather?.current?.weather_code)}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-0.5">
                                        Ressenti {Math.round(weather?.current?.feels_like || weather?.current?.temperature || 0)}°
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-xl bg-muted/40 text-center">
                                    <Droplets className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                                    <p className="text-xs text-muted-foreground">Humidité</p>
                                    <p className="font-bold">{weather?.current?.humidity}%</p>
                                </div>
                                <div className="p-3 rounded-xl bg-muted/40 text-center">
                                    <Wind className="h-5 w-5 text-sky-500 mx-auto mb-1" />
                                    <p className="text-xs text-muted-foreground">Vent</p>
                                    <p className="font-bold">{Math.round(weather?.current?.wind_speed || 0)} km/h</p>
                                </div>
                                <div className="p-3 rounded-xl bg-muted/40 text-center">
                                    <Sun className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                                    <p className="text-xs text-muted-foreground">UV</p>
                                    <p className="font-bold">{weather?.current?.uv_index || 0}</p>
                                    <Badge className={`${getUVLevel(weather?.current?.uv_index || 0).color} text-white text-[10px] mt-0.5`}>
                                        {getUVLevel(weather?.current?.uv_index || 0).level}
                                    </Badge>
                                </div>
                                <div className="p-3 rounded-xl bg-muted/40 text-center">
                                    <Umbrella className="h-5 w-5 text-cyan-500 mx-auto mb-1" />
                                    <p className="text-xs text-muted-foreground">Pluie</p>
                                    <p className="font-bold">{weather?.current?.precipitation_probability}%</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Pool conditions strip: swim index + water temp + evaporation */}
                {weather?.pool && (
                    <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="pool-conditions">
                        {/* Swim index */}
                        <Card className="glass rounded-2xl">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Activity className={`h-5 w-5 text-${weather.pool.swim_index?.color || 'sky'}-500`} />
                                    <span className="font-semibold">Indice de baignade</span>
                                </div>
                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className={`text-4xl font-bold text-${weather.pool.swim_index?.color || 'sky'}-500`}>
                                        {weather.pool.swim_index?.score}
                                    </span>
                                    <span className="text-sm text-muted-foreground">/100</span>
                                </div>
                                <Progress value={weather.pool.swim_index?.score || 0} className="h-2 mb-1" />
                                <p className="text-sm text-muted-foreground">{weather.pool.swim_index?.label}</p>
                            </CardContent>
                        </Card>

                        {/* Water temp */}
                        <Card className="glass rounded-2xl">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Waves className="h-5 w-5 text-sky-500" />
                                    <span className="font-semibold">Eau estimée</span>
                                </div>
                                <div className="flex items-baseline gap-1 mb-2">
                                    <span className="text-4xl font-bold text-sky-500">
                                        {weather.pool.water_temp_estimate}
                                    </span>
                                    <span className="text-lg text-muted-foreground">°C</span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Basé sur les 24h passées (-2°C d&apos;inertie)
                                </p>
                            </CardContent>
                        </Card>

                        {/* Evaporation risk */}
                        <Card className="glass rounded-2xl">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Gauge className={`h-5 w-5 ${weather.pool.evaporation_risk?.level === 'high' ? 'text-red-500' : weather.pool.evaporation_risk?.level === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`} />
                                    <span className="font-semibold">Évaporation</span>
                                </div>
                                <p className={`text-3xl font-bold mb-2 ${weather.pool.evaporation_risk?.level === 'high' ? 'text-red-500' : weather.pool.evaporation_risk?.level === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`}>
                                    {weather.pool.evaporation_risk?.label}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Surveillez le niveau d&apos;eau et la concentration des produits.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* 24h Hourly Chart */}
                {weather?.hourly?.length > 0 && (
                    <Card className="glass rounded-3xl mb-8" data-testid="hourly-chart">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Thermometer className="h-5 w-5 text-primary" />
                                Prévisions 24 heures
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-56 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={weather.hourly.map((h) => ({
                                            hour: new Date(h.time).getHours() + 'h',
                                            temp: Math.round(h.temp),
                                            pluie: h.precip_probability,
                                            uv: h.uv_index,
                                        }))}
                                        margin={{ top: 10, right: 16, left: -8, bottom: 0 }}
                                    >
                                        <defs>
                                            <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.6} />
                                                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.05} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
                                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} />
                                        <Tooltip contentStyle={{ borderRadius: '12px' }} />
                                        <Area yAxisId="left" type="monotone" dataKey="temp" stroke="#f59e0b" fill="url(#tempGrad)" strokeWidth={2} name="Température (°C)" />
                                        <Line yAxisId="right" type="monotone" dataKey="pluie" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Pluie (%)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground mt-2">
                                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500 opacity-60"></span>Température</span>
                                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-sky-500"></span>Probabilité de pluie</span>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* 7-Day Forecast */}
                <Card className="glass rounded-3xl mb-8">
                    <CardHeader>
                        <CardTitle>Prévisions 7 jours</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                            {weather?.forecast?.map((day, index) => (
                                <div
                                    key={index}
                                    className="flex flex-col items-center p-3 rounded-2xl bg-muted/30 hover:bg-muted/60 transition-colors text-center"
                                    data-testid={`forecast-day-${index}`}
                                >
                                    <p className="text-xs font-semibold mb-1 capitalize">{getDayName(day.date)}</p>
                                    {getWeatherIcon(day.weather_code, "h-8 w-8")}
                                    <div className="mt-2">
                                        <p className="font-bold text-lg">{Math.round(day.temp_max)}°</p>
                                        <p className="text-xs text-muted-foreground">{Math.round(day.temp_min)}°</p>
                                    </div>
                                    <div className="flex items-center gap-1 mt-2 text-[11px] text-sky-500">
                                        <Umbrella className="h-3 w-3" />
                                        <span>{day.precip_probability}%</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-[11px] text-amber-500">
                                        <Sun className="h-3 w-3" />
                                        <span>UV {day.uv_index}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Notification Settings */}
                <Card className="glass rounded-3xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Bell className="h-5 w-5" />
                            Paramètres Notifications
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                            <div className="flex items-center gap-4">
                                {notificationsEnabled ? (
                                    <Bell className="h-6 w-6 text-primary" />
                                ) : (
                                    <BellOff className="h-6 w-6 text-muted-foreground" />
                                )}
                                <div>
                                    <Label htmlFor="weather-notif" className="font-medium cursor-pointer">
                                        Notifications Météo
                                    </Label>
                                    <p className="text-sm text-muted-foreground">
                                        Recevez des alertes pour protéger votre piscine
                                    </p>
                                </div>
                            </div>
                            <Switch
                                id="weather-notif"
                                checked={notificationsEnabled}
                                onCheckedChange={toggleNotifications}
                                data-testid="weather-notif-toggle"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Pool Tips based on weather */}
                <Card className="glass rounded-3xl mt-8">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Droplets className="h-5 w-5 text-primary" />
                            Conseils du jour
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {weather?.current?.temperature > 30 && (
                                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                                    <p className="text-sm">
                                        <strong>🌡️ Chaleur :</strong> Augmentez le temps de filtration proportionnellement à la température. 
                                        À {Math.round(weather.current.temperature)}°C, filtrez au moins {Math.round(weather.current.temperature / 2)}h par jour.
                                    </p>
                                </div>
                            )}
                            {weather?.current?.uv_index >= 6 && (
                                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                                    <p className="text-sm">
                                        <strong>☀️ UV élevés :</strong> Le chlore se dégrade plus rapidement. Vérifiez et ajustez le taux 2 fois par jour si nécessaire.
                                    </p>
                                </div>
                            )}
                            {weather?.current?.precipitation_probability > 50 && (
                                <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                    <p className="text-sm">
                                        <strong>🌧️ Pluie prévue :</strong> Après la pluie, vérifiez le pH et l&apos;alcalinité. L&apos;eau de pluie est acide et peut déséquilibrer votre bassin.
                                    </p>
                                </div>
                            )}
                            {weather?.current?.temperature < 30 && weather?.current?.uv_index < 6 && weather?.current?.precipitation_probability <= 50 && (
                                <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                                    <p className="text-sm">
                                        <strong>✅ Conditions idéales :</strong> Profitez de votre piscine ! Les conditions météo sont favorables pour la baignade.
                                    </p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
